import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { SITE_NAME, SITE_URL, usePageSeo } from "@/hooks/usePageSeo";
import { stats, timeline } from "@/mocks/works";
import SubmitForm from "./components/SubmitForm";

const rules = [
  { no: "1", text: "作品需基于真实经历，写清大致时间、场景与关键经过，便于核对与复核。" },
  { no: "2", text: "正文建议控制在 800 至 3000 字之间，主题明确、结构清晰，可以分段叙述。" },
  { no: "3", text: "同一位参赛者可提交多篇不同方向的作品，每篇独立进入作品集并单独计分。" },
  { no: "4", text: "不接受含人身攻击、与内容无关的隐私信息，以及任何形式的商业推广内容。" },
  { no: "5", text: "通过审核的作品会进入对应分类与排行榜，并可能被推荐到首页热门作品区。" },
  { no: "6", text: "请使用固定的投稿邮箱与昵称，方便作品归集、通知结果以及与创作者联系。" },
];

const principles = [
  { icon: "ri-edit-2-line", title: "先记录，再表达", desc: "鼓励先把事实写清楚，再谈感受。秩序清楚了，观点自然站得住。" },
  { icon: "ri-focus-3-line", title: "细节胜过结论", desc: "一张表、一段对话、一个具体数字，往往比一句总结更有说服力。" },
  { icon: "ri-shield-user-line", title: "不伤害无关的人", desc: "曝光的是问题与现象，不针对与事件无关的普通人，不做无必要的指名。" },
  { icon: "ri-chat-check-line", title: "允许被讨论", desc: "作品集开放给读者阅读与讨论，也欢迎作者在后续投稿中补充与修正。" },
];

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const target = document.querySelector(location.hash);
    if (target) {
      window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  usePageSeo({
    title: "大赛说明 - 爆料大赛参赛规则、评审标准与投稿方式",
    description:
      "了解爆料大赛的定位与参赛规则，查看赛程时间线、内容原则与投稿方式，并可通过投稿表单提交参赛作品或咨询合作。",
    keywords: "爆料大赛,爆料大赛参赛规则,爆料大赛怎么投稿,爆料大赛评审标准,爆料大赛作品集",
    path: "/about",
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于爆料大赛",
        url: `${SITE_URL}/about`,
        inLanguage: "zh-CN",
        isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        email: "submit@baoliao-contest.example.com",
        description: "面向大众的公开投稿活动，鼓励把身边的真实观察写下来，并集中展示为参赛作品集。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "大赛说明", item: `${SITE_URL}/about` },
        ],
      },
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="bg-background-100 border-b border-background-200 px-4 md:px-8 py-8 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "大赛说明" }]} />
          <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
                关于爆料大赛
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                爆料大赛是一个面向所有人的公开投稿活动。我们相信，身边那些被忽略的细节值得被认真写下来。所有通过审核的投稿都会被收录进参赛作品集，按方向分类、按热度排序，让好内容不被淹没。
              </p>
            </div>
            <div className="lg:col-span-5">
              <dl className="grid grid-cols-2 gap-4">
                {stats.slice(0, 2).map((s) => (
                  <div key={s.label} className="rounded-lg bg-background-50 border border-background-200 p-4">
                    <dt className="text-xs text-foreground-500">{s.label}</dt>
                    <dd className="mt-2 flex items-baseline gap-1">
                      <span className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                        {s.value}
                      </span>
                      <span className="text-xs text-foreground-500">{s.unit}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <p className="font-label text-xs text-primary-600 tracking-[0.2em] uppercase">About</p>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                我们在做的事，其实很简单
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                把真实发生的事写清楚，然后认真读。没有夸张的标题，也没有刻意制造的情绪，只有一个个具体的时间、场景和细节。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                作品集按六个方向组织内容，读者可以顺着一个方向连续阅读，也可以用关键词直接搜索想看的话题。每一篇作品详情页底部都有相关推荐，方便继续往下翻。
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/works"
                  className="inline-flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium px-5 py-3 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-layout-grid-line" />
                  </span>
                  浏览参赛作品集
                </Link>
                <a
                  href="#submit"
                  className="inline-flex items-center justify-center gap-1.5 bg-background-100 hover:bg-background-200 border border-background-300 text-foreground-800 text-sm font-medium px-5 py-3 rounded-md whitespace-nowrap transition-colors cursor-pointer"
                >
                  直接投稿
                </a>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="w-full h-[280px] md:h-[400px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20scene%20of%20a%20reading%20desk%20with%20open%20notebooks%20stacked%20letters%20and%20a%20small%20brass%20lamp%2C%20deep%20vermillion%20and%20amber%20accents%20on%20cream%20paper%20tones%2C%20soft%20afternoon%20light%2C%20thoughtful%20minimal%20composition%20without%20text&width=1000&height=750&seq=bl-about-intro-01&orientation=landscape"
                  alt="爆料大赛：记录真实观察的写作场景"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="rules" className="scroll-mt-28 bg-background-100 border-y border-background-200 px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="font-label text-xs text-primary-600 tracking-[0.2em] uppercase">Rules</p>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                参赛规则
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                规则不复杂，核心只有一条：写你真实经历过的事，并且把细节写清楚。以下六条是审核时会重点核对的部分。
              </p>
            </div>
            <ol className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {rules.map((rule) => (
                <li key={rule.no} className="flex gap-4 p-5 rounded-lg bg-background-50 border border-background-200">
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading text-sm font-bold">
                    {rule.no}
                  </span>
                  <p className="text-sm text-foreground-700 leading-relaxed">{rule.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-4 md:px-8 py-14 md:py-20">
          <div className="max-w-2xl">
            <p className="font-label text-xs text-primary-600 tracking-[0.2em] uppercase">Principles</p>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
              内容原则
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              这几条原则同时约束着作者、编辑与读者，也是作品集能持续做下去的原因。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {principles.map((p) => (
              <article key={p.title} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 text-xl">
                  <i className={p.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="timeline" className="scroll-mt-28 bg-background-100 border-y border-background-200 px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="font-label text-xs text-primary-600 tracking-[0.2em] uppercase">Schedule</p>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                赛程时间线
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                本期大赛分为征稿、展播、评审与揭晓四个阶段，各阶段时间安排如下。
              </p>
              <div className="mt-6 inline-flex items-center gap-2 text-xs text-primary-700 bg-primary-100 px-3 py-2 rounded-full">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-calendar-check-line" />
                </span>
                当前阶段：征稿期
              </div>
            </div>
            <ol className="lg:col-span-8 flex flex-col">
              {timeline.map((item, index) => (
                <li key={item.phase} className="flex gap-5">
                  <div className="flex flex-col items-center">
                    <span
                      className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-full font-heading text-sm font-bold ${
                        index === 0
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-50 border border-background-300 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    {index < timeline.length - 1 && <span className="w-px flex-1 bg-background-300 my-1" />}
                  </div>
                  <div className="pb-8 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="font-heading text-base font-bold text-foreground-950">{item.phase}</h3>
                      <span className="text-xs text-secondary-900 bg-secondary-100 px-2.5 py-1 rounded-full">
                        {item.date}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="submit" className="scroll-mt-28 px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="font-label text-xs text-primary-600 tracking-[0.2em] uppercase">Submit</p>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">
                投稿与咨询
              </h2>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                填写右侧表单即可提交参赛作品。如果稿件较长，也可以把全文发送到下面的邮箱，标题注明「投稿 + 作品方向」。
              </p>
              <ul className="mt-7 flex flex-col gap-4">
                <li className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-primary-600 text-lg">
                    <i className="ri-mail-send-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">投稿与合作邮箱</span>
                    <span className="mt-0.5 block text-sm text-foreground-900 break-all">
                      submit@baoliao-contest.example.com
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-primary-600 text-lg">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">审核时间</span>
                    <span className="mt-0.5 block text-sm text-foreground-900">提交后三个工作日内完成初审</span>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-primary-600 text-lg">
                    <i className="ri-message-3-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">咨询方式</span>
                    <span className="mt-0.5 block text-sm text-foreground-900">
                      通过表单留言，或直接发送邮件说明问题
                    </span>
                  </span>
                </li>
              </ul>
              <Link
                to="/works"
                className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                先看看往期作品找灵感
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
            </div>
            <div className="lg:col-span-8">
              <SubmitForm />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}